import Vue from 'vue'
import {
  layerSettingsFactory,
  infoLayerSettingsFactory,
  zonegroupLayerSettingsFactory,
  admLevelLayerSettingsFactory,
  zoneLayerSettingsFactory
} from './layerSettingsFactory'
import { getInfrastructureObjectLegendIcon } from '@/helpers/utils'
import { DEFAULT_CLUSTER_STYLE } from '@/store/objects/defaultLayerStyles'
//Это нужно чтобы обернуть иконки инфослоев в белый круг
import { toDataURL, svgLeafletInfo } from '@/helpers/utils'

/**Соответствие типов инфослоев на клиенте и на сервере */
const INFO_LAYER_TYPES_MAP = {
  point: 'info',
  linear: 'streaminfo'
}

/**Преобразование названий для слоев */
const LAYER_OBJECT_NAME_MAP = {
  building: (buildingItem) => `Здание №${buildingItem.id}`,
  source: (sourceItem) => `Источник №${sourceItem.id}`,
  flow: (flowItem) => {
    const source = flowItem.source ||
      flowItem.source_infrastructure || {
        id: null,
        name: 'Источник неизвестен'
      }
    flowItem.infrastructure = flowItem.infrastructure || { id: null, name: 'Объект инфраструктуры неизвестен' }
    return `ID: ${flowItem.id} от ${source.name} к ${flowItem.infrastructure.name}`
  },
  transfer: (transferItem) => `Маршрут перевозки №${transferItem.id}`,
  collectingloop: (collectingloopItem) => `Петля сбора №${collectingloopItem.id}`,
  removaltrip: (removaltripItem) => `Маршрут вывоза №${removaltripItem.id}`,
  emitter: (emitterItem) => `Отходообразователь №${emitterItem.id}`
}

/**Статусы объектов инфраструктуры */
const INFRASTRUCTURE_STATUSES = {
  active: {
    id: 'active',
    name: 'Действующие',
    order: 1000,
    labels: {
      collapseButton: 'Закрыть список действующих объектов инфраструктуры',
      expandButton: 'Открыть список действующих объектов инфраструктуры'
    }
  },
  planned: {
    id: 'planned',
    name: 'Планируемые к вводу',
    order: 2000,
    labels: {
      collapseButton: 'Закрыть список планируемых к выводу объектов инфраструктуры',
      expandButton: 'Открыть список планируемых к выводу объектов инфраструктуры'
    }
  },
  closed: {
    id: 'closed',
    name: 'Вышедшие из эксплуатации',
    order: 3000,
    labels: {
      collapseButton: 'Закрыть список вышедших из эксплуатации объектов инфраструктуры',
      expandButton: 'Открыть список вышедших из эксплуатации объектов инфраструктуры'
    }
  },
  awaiting: {
    id: 'awaiting',
    name: 'Ожидающие расчет',
    order: 4000,
    labels: {
      collapseButton: 'Закрыть список ожидающих расчета объектов инфраструктуры',
      expandButton: 'Открыть список ожидающих расчета объектов инфраструктуры'
    }
  },
  unused: {
    id: 'unused',
    name: 'Неиспользуемые',
    order: 5000,
    labels: {
      collapseButton: 'Закрыть список неиспользуемых объектов инфраструктуры',
      expandButton: 'Открыть список неиспользуемых объектов инфраструктуры'
    }
  }
}

const INFRASTRUCTURE_TYPE_LABEL_MAP = {
  transfer: {
    collapseButton: 'Закрыть список объектов перегрузки',
    expandButton: 'Открыть список объектов перегрузки'
  },
  assortment: {
    collapseButton: 'Закрыть список объектов обработки (сортировки)',
    expandButton: 'Открыть список объектов обработки (сортировки)'
  },
  recast: {
    collapseButton: 'Закрыть список объектов МПЗ',
    expandButton: 'Открыть список объектов МПЗ'
  },
  disposal: {
    collapseButton: 'Закрыть список объектов обезвреживания',
    expandButton: 'Открыть список объектов обезвреживания'
  },
  placement: {
    collapseButton: 'Закрыть список объектов размещения',
    expandButton: 'Открыть список объектов размещения'
  },
  recycle: {
    collapseButton: 'Закрыть список объектов утилизации',
    expandButton: 'Открыть список объектов утилизации'
  }
}

const IGNORE_IN_LOAD_MAP_DATA = ['flow', 'source']

export default {
  /**
   * Подготовка дочерних слоев инфраструктуры для распределения по статусам и типам
   * @param {*} param0
   * @param {*} param1
   */
  prepareInfrastructureLayerSettings({ commit, getters }, { layerId, infrastructureLayerData }) {
    const infrastructureLayerSettings = getters.objectLayerSettings[layerId]
    if (!infrastructureLayerSettings) {
      return
    }

    //Очиста общего словаря с ОИ левой панели
    commit('clearDataSourceObjects', {
      dataSourceId: 'infrastructure'
    })

    //Доступные типы объектов инфраструктуры
    const infrastructureTypes = getters.settings.infrastructure_type

    //Распределение загруженных объектов инфраструктуры по специальному словарю
    let layerObjectsByStatusAndType = {}
    infrastructureLayerData.results.forEach((layerObject) => {
      //По текущей структуре невозможно распределить ОИ без статуса
      if (!layerObject.status) {
        return
      }

      const layerObjectStatus = layerObject.status
      const layerObjectType = infrastructureTypes.filter((infrastructureTypeItem) => {
        return layerObject.rank >= infrastructureTypeItem.min_rank && layerObject.rank < infrastructureTypeItem.max_rank
      })[0].label

      if (!layerObjectsByStatusAndType[layerObjectStatus]) {
        layerObjectsByStatusAndType[layerObjectStatus] = {}
      }
      if (!layerObjectsByStatusAndType[layerObjectStatus][layerObjectType]) {
        layerObjectsByStatusAndType[layerObjectStatus][layerObjectType] = []
      }

      //Объект слоя для левой панели
      let layerObjectForLeftPanel = {
        ...layerObject,
        visibility: true,
        zoneVisibility: false
      }

      //Определение видимости, если такой объект уже загружался установка видимости
      const statusLayerId = `${layerId}-${layerObjectStatus}`
      const statusAndTypeLayerId = `${statusLayerId}-${layerObjectType}`
      let existingObject = getters.getLayerObject(statusAndTypeLayerId, layerObject.id)
      if (existingObject) {
        layerObjectForLeftPanel.visibility = existingObject.visibility
        layerObjectForLeftPanel.zoneVisibility = existingObject.zoneVisibility
      } else {
        const layerSettings = getters.objectLayerSettings[statusAndTypeLayerId] || getters.objectLayerSettings[statusLayerId] || infrastructureLayerSettings
        layerObjectForLeftPanel.visibility = layerSettings.visibility
        layerObjectForLeftPanel.zoneVisibility = layerSettings.zoneVisibility
      }

      //TODO: нужна фабрика объектов слоя на основе данных от сервера
      layerObjectsByStatusAndType[layerObjectStatus][layerObjectType].push(layerObjectForLeftPanel)

      //Сохранение объекта в единый источник данных ОИ левой панели
      commit('updateDataSourceObject', {
        dataSourceId: 'infrastructure',
        objectId: layerObjectForLeftPanel.id,
        objectData: layerObjectForLeftPanel
      })
    })

    //Очистка объектов левой панели (Очистка слоев)
    getters.getLayerSettingsByDataSource('infrastructure').forEach((infrastructureLayerSettings) => {
      commit('initLayerObjects', { layerId: infrastructureLayerSettings.id })
    })

    //На основе специального словаря инициализируем структуру дочерних слоев
    Object.entries(layerObjectsByStatusAndType).forEach(([status, typesInStatus]) => {
      const statusLayerId = `${layerId}-${status}`
      const statusSettings = INFRASTRUCTURE_STATUSES[status]
      const statusLayerSettings = getters.objectLayerSettings[statusLayerId]

      //Сохранение настроек слоя в общий словарь настроек слоев
      if (!statusLayerSettings) {
        commit('setObjectLayerSettingsItem', {
          layerSettings: layerSettingsFactory({
            id: statusLayerId,
            parentId: layerId,
            groupLayer: true,
            name: statusSettings.name,
            order: statusSettings.order,
            visibility: infrastructureLayerSettings.visibility,
            zoneVisibility: infrastructureLayerSettings.zoneVisibility,
            canToggleItemsVisibility: false,
            scrollable: false,
            showChildLayerObjectsCount: true,
            childrenDraggable: false,

            labels: {
              ...statusSettings.labels
            }
          })
        })
      }

      Object.entries(typesInStatus).forEach(([type, layerObjectsArray]) => {
        const statusAndTypeLayerId = `${statusLayerId}-${type}`
        const typeSettings = infrastructureTypes.find((typeItem) => typeItem.label === type)
        const statusAndTypeLayerSettings = getters.objectLayerSettings[statusAndTypeLayerId]

        //Сохранение настроек слоя в общий словарь настроек слоев
        if (!statusAndTypeLayerSettings) {
          commit('setObjectLayerSettingsItem', {
            layerSettings: layerSettingsFactory({
              id: statusAndTypeLayerId,
              parentId: statusLayerId,
              name: typeSettings.name,
              order: typeSettings.min_rank * 1000,
              visibility: statusLayerSettings ? statusLayerSettings.visibility : infrastructureLayerSettings.visibility,
              zoneVisibility: statusLayerSettings ? statusLayerSettings.zoneVisibility : infrastructureLayerSettings.zoneVisibility,
              canToggleItemsVisibility: true,
              scrollable: false,
              dataSource: 'infrastructure',
              type: 'infrastructure',
              //Иконка в левой панели в зависимости от типа и статуса объекта инфраструктуры
              iconClass: getInfrastructureObjectLegendIcon(type, status),

              hideChangingAttributes: infrastructureLayerSettings.hideChangingAttributes || false,

              //TODO:
              labels: {
                ...INFRASTRUCTURE_TYPE_LABEL_MAP[typeSettings.label]
              }
            })
          })
        }

        //Подготовка vuex для хранения объектов слоев
        commit('initLayerObjects', {
          layerId: statusAndTypeLayerId
        })
        //Простановка количества объектов
        commit('setLayerObjectsCount', {
          layerId: statusAndTypeLayerId,
          count: layerObjectsArray.length
        })
        //Установка объектов слоя
        commit('updateLayerObjects', {
          layerId: statusAndTypeLayerId,
          layerObjectsArray: layerObjectsArray
        })
      })
    })
  },

  /**
   * Формирование настроек информационных слоев во vuex на основе информации от сервера
   * @param {*} param0
   * @param {*} param1
   */
  prepareInfoLayerSettings({ commit, dispatch, getters }, { infoLayers }) {
    const finishedInfoLayers = infoLayers.filter((infoLayerItem) => {
      return infoLayerItem.status === 'finished' && !infoLayerItem.is_integration
    })

    //Формирование массива с настройками инфослоев
    const infoLayerSettings = finishedInfoLayers
      .filter((finishedInfoLayerItem) => {
        const infoLayerType = INFO_LAYER_TYPES_MAP[finishedInfoLayerItem.layer_type]
        return !!getters.objectLayerSettings[infoLayerType]
      })
      .map((finishedInfoLayerItem, index) => {
        const infoLayerType = INFO_LAYER_TYPES_MAP[finishedInfoLayerItem.layer_type]
        const id = `${infoLayerType}-${finishedInfoLayerItem.id}`
        const existedLayer = getters.objectLayerSettings[id]
        return infoLayerSettingsFactory({
          id: id,
          parentId: infoLayerType,
          styleId: id,
          mapIconId: finishedInfoLayerItem.icon, //Иконка для условного обозначения на карте
          serverId: finishedInfoLayerItem.id, //Надо запомнить id от сервера, чтобы потом нормально данные запросить
          order: finishedInfoLayerItem.order || index * 1000,
          type: infoLayerType,
          name: finishedInfoLayerItem.name,
          visibility: existedLayer ? existedLayer.visibility : finishedInfoLayerItem.active,
          zoneVisibility: existedLayer ? existedLayer.zoneVisibility : false,
          canToggleItemsVisibility: infoLayerType === 'streaminfo',
          scrollable: true,
          color: finishedInfoLayerItem.color,
          headers: finishedInfoLayerItem.headers,

          //Специальный флаг, чтобы отличить инфослой от остальных и не парсить идентификатор на предмет наличия "info"
          isInfoLayer: true,

          /**При клике на линию чувствительность в пикселях */
          tolerance: infoLayerType === 'streaminfo' ? 5 : 0,

          is_trip: finishedInfoLayerItem.is_trip,

          labels: finishedInfoLayerItem.labels
        })
      })

    infoLayerSettings.forEach((infoLayerSettingsItem) => {
      //Инициализация стилей слоев
      //TODO: желательно, чтобы стили приходили с бэка
      let styleConfig
      switch (infoLayerSettingsItem.type) {
        case 'info':
          const iconConfig = getters.infoLayerIconDictionary[infoLayerSettingsItem.mapIconId] || getters.defaultInfoLayerIcon
          styleConfig = [
            {
              type: 'CanvasMarker',
              filter: ['==', ['get', 'count'], 1],
              options: {
                img: {
                  icon: iconConfig ? iconConfig.id : 'infolayer_default',
                  url: iconConfig ? iconConfig.icon_file.file : svgLeafletInfo(),
                  size: ['literal', [46, 46]],
                  offset: { x: 0, y: 0 }
                }
              }
            },
            ...DEFAULT_CLUSTER_STYLE
          ]
          break
        case 'streaminfo':
          styleConfig = {
            type: 'GeoJSON',
            options: {
              bubblingMouseEvents: false,
              style: {
                color: ['case', ['==', ['get', 'isSelected'], true], 'red', infoLayerSettingsItem.color || 'blue'],
                weight: 3,
                opacity: 1
              }
            }
          }
          break
        default:
          break
      }
      commit('updateLayerStyle', {
        styleId: infoLayerSettingsItem.id,
        styleConfig
      })

      //Сохранение настроек слоя в общий словарь настроек слоев
      commit('setObjectLayerSettingsItem', {
        layerSettings: infoLayerSettingsItem
      })

      //Подготовка vuex для хранения объектов слоев
      commit('initLayerObjects', {
        layerId: infoLayerSettingsItem.id
      })

      //Запрос данных информационного слоя
      dispatch('loadInfoLayerObjects', {
        layerId: infoLayerSettingsItem.id
      })
    })
  },

  /**
   * Загрузка объектов информационного слоя
   * @param {*} param0
   * @param {*} param1
   */
  loadInfoLayerObjects({ commit, getters }, { layerId }) {
    const layerSettings = getters.objectLayerSettings[layerId]

    //Получение текущих объектов слоя
    const currentLayerObjects = getters.layerObjects[layerId]
    const currentLayerObjectsLength = Object.keys(currentLayerObjects.objects).filter((key) => !currentLayerObjects.objects[key].isNotFull).length

    //Если загружены все объекты то ничего не запрашиваем
    if (currentLayerObjects.count && currentLayerObjectsLength === currentLayerObjects.count) {
      return Promise.resolve('all')
    }

    commit('setObjectLoadingData', 1)
    return Vue.http
      .get(getters.endpoint.excelLayers.layerObjectList, {
        params: {
          is_panel: true, //Дополнительный параметр, чтобы игнорировать скрытые поля и корректно отображать названия
          limit: 15,
          offset: currentLayerObjectsLength,
          id: layerSettings.serverId,
          workspace: getters.workspace,
          version: getters.version,
          ...getters.objectFilterRequestParams
        }
      })
      .then((response) => {
        //Массив полученных объектов слоя
        const layerObjectsArray = response.body.result.map((resultItem) => {
          const existingLayerObject = currentLayerObjects.objects[resultItem.id] || {}
          delete existingLayerObject.isNotFull

          return {
            id: resultItem.id,
            visibility: layerSettings.visibility,
            zoneVisibility: layerSettings.zoneVisibility,
            name:
              resultItem.name ||
              resultItem['Название'] ||
              resultItem['Наименование'] ||
              resultItem.address ||
              resultItem['Адрес'] ||
              'Объект ID ' + resultItem.id,
            ...existingLayerObject
          }
        })

        //Предполагается что словарь с объектами слоя проинициализировали
        //и мы просто добавим в него объекты
        commit('updateLayerObjects', {
          layerId,
          layerObjectsArray
        })

        commit('setLayerObjectsCount', {
          layerId,
          count: response.body.count
        })
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        commit('setObjectLoadingData', 0)
      })
  },

  /**
   * Инициализация слоев территориального деления
   * @param {*} param0
   * @param {*} param1
   */
  setInitialZonegroupStructure({ commit, getters }, { layerId, zonegroupServerData, zonegroupInfoServerData }) {
    if (!getters.objectLayerSettings[layerId]) {
      return
    }

    /**Настройки цветов для групп зон */
    // поддержка для хмао - в публичных вс при наличии настройки hidePublicTransport скрывать от людей operator_zone
    zonegroupServerData.results = zonegroupServerData.results.filter(
      (zonegroup) => getters.canEditWs || !getters.objectLayerSettings.zonegroup.hidePublicTransport || !zonegroup.operator_zone
    )

    zonegroupServerData.results
      .sort((zonegroup1, zonegroup2) => {
        return zonegroup1.id - zonegroup2.id
      })
      .forEach((zonegroupItem, index) => {
        const zonegroupLayerId = `${layerId}-${zonegroupItem.id}`

        //Настройки, которые необходимо восстановить после перезагрузки объектов
        let zonegroupSettingsForRestore = {}
        let zonegroupExistingLayerSettings = getters.objectLayerSettings[zonegroupLayerId]
        if (zonegroupExistingLayerSettings) {
          zonegroupSettingsForRestore = {
            visibility: zonegroupExistingLayerSettings.visibility,
            zoneVisibility: zonegroupExistingLayerSettings.zoneVisibility,
            open: zonegroupExistingLayerSettings.open,
            selectedColor: zonegroupExistingLayerSettings.selectedColor
          }
        }

        //Слой для группы зон
        commit('setObjectLayerSettingsItem', {
          layerSettings: zonegroupLayerSettingsFactory({
            id: zonegroupLayerId,

            //На этот тип много всего завязано, TODO: подумать как от него отказаться
            type: 'zonegroup',

            parentId: layerId,
            groupLayer: true,
            name: zonegroupItem.name,
            order: index * 1000,
            visibility: zonegroupItem.default,
            zoneVisibility: true,
            canToggleItemsVisibility: false,
            scrollable: false,
            showChildLayerCount: true,
            childrenDraggable: false,
            default: zonegroupItem.default,
            operator_zone: zonegroupItem.operator_zone,
            serverId: zonegroupItem.id,
            filterByText: getters.objectLayerSettings.zonegroup.filterByText,

            //Это тип доступных раскрасок
            coloringType: 'zonegroup',
            //Выбранная цветовая раскраска для группы зон
            selectedColor: zonegroupItem.default ? getters.objectLayerSettings[layerId].selectedColor || 'default' : 'default',

            labels: {
              collapseButton:
                zonegroupItem.name === 'Административное деление'
                  ? 'Закрыть список зон административного деления'
                  : `Закрыть список зон "${zonegroupItem.name}"`,
              expandButton:
                zonegroupItem.name === 'Административное деление'
                  ? 'Открыть список зон административного деления'
                  : `Открыть список зон "${zonegroupItem.name}"`
            },

            ...zonegroupSettingsForRestore
          })
        })

        zonegroupItem.adm_levels.forEach((admLevelId) => {
          const admLevelItem = getters.settings.administrative_levels.find(({ adm_level }) => admLevelId === adm_level)
          if (!admLevelItem) {
            return
          }

          const amdLevelLayerId = `${zonegroupLayerId}-${admLevelId}`

          //Определение видимости слоя уровня территориального деления внутри группы зон
          let amdLevelLayerVisibility = zonegroupItem.default
          let amdLevelLayerZoneVisibility = true
          let admLevelLayerIsOpen = false
          const amdLevelExistingLayerSettings = getters.objectLayerSettings[amdLevelLayerId]
          if (amdLevelExistingLayerSettings) {
            amdLevelLayerVisibility = amdLevelExistingLayerSettings.visibility
            amdLevelLayerZoneVisibility = amdLevelExistingLayerSettings.zoneVisibility
            admLevelLayerIsOpen = amdLevelExistingLayerSettings.open
          }

          //Слой для уровня территориального деления внутри группы зон
          commit('setObjectLayerSettingsItem', {
            layerSettings: admLevelLayerSettingsFactory({
              id: amdLevelLayerId,
              parentId: zonegroupLayerId,
              groupLayer: true,
              name: admLevelItem.name,
              order: admLevelId * 1000,
              visibility: amdLevelLayerVisibility,
              zoneVisibility: amdLevelLayerZoneVisibility,
              open: admLevelLayerIsOpen,
              canToggleItemsVisibility: false,
              toggleSiblingVisibility: true,
              scrollable: false,
              showChildLayerCount: true,
              childrenDraggable: false,
              filterByText: getters.objectLayerSettings.zonegroup.filterByText,

              //Административный уровень
              admLevel: admLevelId,

              labels: {
                collapseButton: `Закрыть уровень административного деления "${admLevelItem.name}"`,
                expandButton: `Открыть уровень административного деления "${admLevelItem.name}"`
              }
            })
          })
        })
      })

    Object.entries(zonegroupInfoServerData)
      .filter(([zonegroupAmdLevelId, zonegroupInfoItem]) => zonegroupServerData.results.find((zoneGroup) => zoneGroup.id === zonegroupInfoItem.id))
      .forEach(([zonegroupAmdLevelId, zonegroupInfoItem]) => {
        Object.entries(zonegroupInfoItem.zones_info).forEach(([zoneInfoKey, zoneInfoItem]) => {
          const amdLevelLayerId = zonegroupAmdLevelId.replace(/_/g, '-')
          const zoneLayerId = `${amdLevelLayerId}-${zoneInfoItem.id}`

          //Определение видимости слоя зоны
          let visibility = getters.objectLayerSettings[amdLevelLayerId].visibility
          let zoneVisibility = getters.objectLayerSettings[amdLevelLayerId].zoneVisibility

          const zoneExistingLayerSettings = getters.objectLayerSettings[zoneLayerId]
          let zoneIsOpen = false
          if (zoneExistingLayerSettings) {
            visibility = zoneExistingLayerSettings.visibility
            zoneVisibility = zoneExistingLayerSettings.zoneVisibility
            zoneIsOpen = zoneExistingLayerSettings.open
          } else {
            //Это включение дефолтного уровня тер деления
            const zonegroupLayerId = `${layerId}-${zoneInfoItem.group}`
            const zonegroupLayerSettings = getters.objectLayerSettings[zonegroupLayerId]

            if (zonegroupLayerSettings.default) {
              //Если есть специальная настройка, то включаем уровень согласно этой настройке
              const defaultAdmLevel = getters.objectLayerSettings.zonegroup.defaultAdmLevel
              if (defaultAdmLevel) {
                //>= специально, чтобы отображать видимость в легенде видимого уровня и тех что под ним
                visibility = getters.objectLayerSettings[amdLevelLayerId].admLevel >= defaultAdmLevel
              } else if (zonegroupLayerSettings) {
                //Если спец-настройки нет то это административное деление и уровень в котором больше одной территории
                visibility = visibility && zoneInfoItem.territories.length > 1
              }
            }
          }

          commit('setObjectLayerSettingsItem', {
            layerSettings: zoneLayerSettingsFactory({
              id: zoneLayerId,
              parentId: amdLevelLayerId,
              name: zoneInfoItem.name,
              order: zoneInfoItem.id * 1000,
              serverId: zoneInfoItem.id,
              visibility: visibility,
              zoneVisibility: zoneVisibility,
              open: zoneIsOpen,
              canToggleItemsVisibility: false,
              scrollable: false,
              dataSource: 'zonegroup',
              type: 'zone',
              selectable: true,
              //Идентификатор цвета зоны по умолчанию
              colorId: zoneInfoItem.color,

              //Скрытие доп атрибутов зависит от настроект корневой папки zonegroup (Территориальное деление)
              hideChangingAttributes: getters.objectLayerSettings[layerId].hideChangingAttributes,

              filterByText: getters.objectLayerSettings.zonegroup.filterByText,

              labels: {
                collapseButton: 'Выбрать зону и закрыть список территорий',
                expandButton: 'Выбрать зону и открыть список территорий'
              }
            })
          })

          //Определение уровня административного деления по родительскому слою,
          //актуально если adm_level скрыти в публичном ws (https://app.clickup.com/t/2t7u5yx)
          const admLevelByLayerId = amdLevelLayerId.split('-')[2]

          //Формирование массива объектов слоя зон для левой панели
          const layerObjectsArray = (zoneInfoItem.territories || []).map((territoryItem) => {
            const admLevel = territoryItem.adm_level || admLevelByLayerId || 20

            let result = {
              ...territoryItem,
              adm_level: admLevel,
              type: admLevel < 20 ? 'adm_territory' : 'dedicated_territory',
              visibility: visibility,
              zoneVisibility: zoneVisibility
            }

            return result
          })

          commit('initLayerObjects', {
            layerId: zoneLayerId
          })
          commit('updateLayerObjects', {
            layerId: zoneLayerId,
            layerObjectsArray
          })
          commit('setLayerObjectsCount', {
            layerId: zoneLayerId,
            count: (zoneInfoItem.territories || []).length
          })
        })
      })
  },

  /**
   * Загрузка объектов слоя для левой панели
   * @param {*} param0
   * @param {*} param1
   */
  loadLayerObjects({ commit, getters, dispatch }, { layerId, isNext }) {
    const layerSettings = getters.objectLayerSettings[layerId]
    if (!layerSettings) {
      return
    }

    //Получение текущих объектов слоя
    let currentLayerObjects = getters.layerObjects[layerId]
    if (!currentLayerObjects) {
      //Проинициализируем объекты слоя левой панели если они не проинициализированны
      commit('initLayerObjects', {
        layerId
      })
      currentLayerObjects = getters.layerObjects[layerId]
    }

    // кол-во ключей за исключением временных объектов, созданных во время редактирования
    // видимости до их прогрузки (из правой панели)
    const currentLayerObjectsLength = Object.keys(currentLayerObjects.objects).filter((key) => !currentLayerObjects.objects[key].isNotFull).length

    //Если загружены все объекты то ничего не запрашиваем
    if (isNext && currentLayerObjects.count && currentLayerObjectsLength === currentLayerObjects.count) {
      return Promise.resolve('all')
    }

    //Формирование дополнительных параметров для слоя "Потоки отходов" (flow)
    //По возможности подумать как убрать такой частный случай
    let additionalParams = {}
    if (layerId === 'flow' || layerId === 'source') {
      //Проблемы
      const flowLayerSettings = getters.objectLayerSettings.flow
      const problemArray = flowLayerSettings ? flowLayerSettings.selectedProblems || [] : []
      const transportTypes = flowLayerSettings ? flowLayerSettings.selectedTransportTypes || [] : []
      additionalParams.problems = problemArray.join(',')
      if (transportTypes.length > 0) {
        additionalParams.transport_types = transportTypes.join(',')
      }

      //Уровень административного деления
      const level = layerSettings.selectedLevel
      if (level && level.length === 1) {
        additionalParams.level = level[0]
      }

      //Период
      if (getters.objectsPeriod.scaleType.id === 'period') {
        additionalParams.period_id = getters.objectsPeriod.borders[0]
      }
    }

    commit('setObjectLoadingData', 1)
    return Vue.http
      .get(getters.endpoint.entities[layerId], {
        params: {
          is_panel: true, //Дополнительный параметр, чтобы игнорировать скрытые поля и корректно отображать названия
          workspace: getters.workspace,
          version: getters.version,
          limit: isNext ? 15 : Math.max(currentLayerObjectsLength, 15),
          offset: isNext ? currentLayerObjectsLength : 0,
          fields: layerSettings.fields,
          [getters.objectsPeriod.scaleType.id]: getters.objectsPeriod.borders.join(','),
          ...getters.objectFilterRequestParams,
          ...additionalParams
        }
      })
      .then((response) => {
        return dispatch('updateDefaultLayerObjects', {
          layerId,
          layerObjectsFromServer: response.body,
          clear: !isNext
        })
      })
      .finally(() => {
        commit('setObjectLoadingData', 0)
      })
  },

  /**
   * Загрузка информации по идентификатору объекта и обновление данных объекта в левой панели
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  loadLayerObjectById({ commit, getters }, { layerId, objectId }) {
    commit('setObjectLoadingData', 1)
    return Vue.http
      .get(getters.endpoint[`${layerId}ById`](objectId), {
        params: {
          workspace: getters.workspace,
          version: getters.version,
          [getters.objectsPeriod.scaleType.id]: getters.objectsPeriod.borders.join(',')
        }
      })
      .then((response) => {
        const layerItem = response.body

        commit('updateLayerObjectById', {
          layerId,
          objectId,
          objectData: {
            //TODO: фабрика объекта в зависимости от слоя
            ...layerItem,
            name: layerItem.name || layerItem.address || LAYER_OBJECT_NAME_MAP[layerId](layerItem)
          }
        })
      })
      .finally(() => {
        commit('setObjectLoadingData', 0)
      })
  },

  /**
   * Создание объекта слоя в БД
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  createLayerObject({ getters }, { layerId, objectData }) {
    return Vue.http.post(getters.endpoint[layerId], objectData, {
      params: {
        workspace: getters.workspace,
        version: getters.version,
        [getters.objectsPeriod.scaleType.id]: getters.objectsPeriod.borders.join(',')
      }
    })
  },

  /**
   * Создание объекта слоя с перезапросом данных карты и левой панели
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  createLayerObjectWithReload({ dispatch }, { layerId, objectData }) {
    return dispatch('createLayerObject', {
      layerId,
      objectData
    }).then((result) => {
      //Перезагрузка данных карты
      dispatch('getMapDataByLayoutType', { type: layerId })

      //Перезагрузка данных для левой панели
      dispatch('loadLayerObjects', { layerId })

      return result.body
    })
  },

  /**
   * Обновление (сохранение изменений в БД) данных объекта слоя
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  updateLayerObject({ getters }, { layerId, objectId, objectData }) {
    return Vue.http.patch(getters.endpoint[`${layerId}ById`](objectId), objectData, {
      params: {
        workspace: getters.workspace,
        version: getters.version,
        [getters.objectsPeriod.scaleType.id]: getters.objectsPeriod.borders.join(',')
      }
    })
  },

  /**
   * Обновление (сохранение изменений в БД) данных объекта слоя
   * После этого дополнительно вызывается обновление данных в левой панели и на карте
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  updateLayerObjectWithReload({ getters, dispatch }, { layerId, objectId, objectData }) {
    return dispatch('updateLayerObject', {
      layerId,
      objectId,
      objectData
    }).then((result) => {
      dispatch('getMapDataByLayoutType', { type: layerId })

      if (getters.layerObjects[layerId] && getters.layerObjects[layerId].objects[objectId]) {
        dispatch('loadLayerObjectById', {
          layerId,
          objectId
        })
      }

      return result.body
    })
  },

  /**
   * Удаление объекта слоя из БД
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  deleteLayerObject({ getters }, { layerId, objectId }) {
    return Vue.http.delete(getters.endpoint[`${layerId}ById`](objectId), {
      params: {
        workspace: getters.workspace,
        version: getters.version,
        [getters.objectsPeriod.scaleType.id]: getters.objectsPeriod.borders.join(',')
      }
    })
  },

  /**
   * Удаление объекта слоя из БД
   * После этого дополнительно вызывается обновление данных в левой панели и на карте
   * @param {*} param0
   * @param {*} param1
   */
  deleteLayerObjectWithReload({ getters, dispatch }, { layerId, objectId }) {
    dispatch('deleteLayerObject', {
      layerId,
      objectId
    }).then(() => {
      //Перезагрузка данных карты
      dispatch('getMapDataByLayoutType', { type: layerId })

      if (getters.layerObjects[layerId] && getters.layerObjects[layerId].objects[objectId]) {
        //Перезагрузка данных для левой панели, если объект был в данных левой панели
        dispatch('loadLayerObjects', { layerId })
      }
    })
  },

  /**
   * Установка данных объектов слоя для левой панели
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  updateDefaultLayerObjects({ commit, getters }, { layerId, layerObjectsFromServer, clear = false }) {
    const layerSettings = getters.objectLayerSettings[layerId]

    if (!layerSettings) {
      return
    }

    //Подготовка vuex для хранения объектов слоев
    //clear - параметр, флаг принудительной очистки объектов
    if (!getters.layerObjects[layerId] || clear) {
      commit('initLayerObjects', {
        layerId: layerId
      })
    }

    const currentLayerObjects = getters.layerObjects[layerId].objects

    //Преобразование названий объектов слоя
    const layerObjectsArray = layerObjectsFromServer.results.map((layerItem) => {
      const currentLayerObject = currentLayerObjects[layerItem.id] || {}
      delete currentLayerObject.isNotFull

      let name = layerItem.name || layerItem.address || ''
      if (!name && LAYER_OBJECT_NAME_MAP[layerId]) {
        name = LAYER_OBJECT_NAME_MAP[layerId](layerItem)
      }

      return {
        ...layerItem,
        visibility: layerSettings.visibility,
        zoneVisibility: layerSettings.zoneVisibility,
        ...currentLayerObject,
        name
      }
    })

    //Предполагается что словарь с объектами слоя проинициализировали
    //и мы просто добавим в него объекты
    commit('updateLayerObjects', {
      layerId,
      layerObjectsArray
    })

    commit('setLayerObjectsCount', {
      layerId,
      count: layerObjectsFromServer.count
    })
  },

  /**
   * Скрыть все слои
   * @param {*} param0
   */
  hideAllLayers({ dispatch, getters }) {
    getters.rootLayerSettings.forEach((layerSettings) => {
      dispatch('setLayerVisibility', {
        layerId: layerSettings.id,
        visibility: false,
        zoneVisibility: false
      })
    })
  },

  /**
   * Восстановить видимость слоев по умолчанию
   * @param {*} param0
   */
  resetDefaultLayerVisibility({ dispatch, getters }) {
    getters.simpleLayerSettings.forEach((layerSettings) => {
      dispatch('setLayerVisibility', {
        layerId: layerSettings.id,
        visibility: layerSettings.defaultSettings.visibility,
        zoneVisibility: layerSettings.defaultSettings.zoneVisibility
      })
    })
  },

  /**
   * Установка видимости слоя
   * @param {*} param0
   * @param {*} param1
   */
  setLayerVisibility({ commit, dispatch, getters }, { layerId, visibility, checkSingleChildVisibility = false, toggleSiblingVisibility = false }) {
    //Если у родительского слоя режим видимости только одного дочернего слоя и включаем видимость,
    //то надо отключить видимость "братских" слоев
    //актуально только для режима окна объектов, в режиме редактирования зон подход к включению групп зон стандартный
    if (visibility && checkSingleChildVisibility && !getters.objectZoneGroupMode) {
      dispatch('hideSiblingsForSingleChildVisibleLayer', {
        layerId
      })
    }

    //Костыль для территориального деления, скрывает предыдущие по порядку слои или показывает следующие
    const layerSettings = getters.objectLayerSettings[layerId]
    if (!layerSettings) {
      return
    }
    if (toggleSiblingVisibility) {
      if (visibility) {
        dispatch('showNextSiblings', { layerId })
      } else {
        dispatch('hidePrevSiblings', { layerId })
      }
    }

    const childrenLayerSettings = getters.getLayerSettingsByParentId(layerId) || []
    if (childrenLayerSettings.length > 0) {
      if (visibility && layerSettings.singleChildVisibility) {
        //Включение видимости только одного слоя для режима singleChildVisibility
        dispatch('setLayerVisibility', {
          layerId: childrenLayerSettings[0].id,
          visibility
        })
        return
      }

      childrenLayerSettings.forEach((childLayerSettings) => {
        dispatch('setLayerVisibility', {
          layerId: childLayerSettings.id,
          visibility
        })
      })
    }
    //Установка видимости в настройках слоя
    commit('setLayerVisibility', {
      layerId,
      visibility,
      visibilityProperty: getters.objectVisibilityProperty
    })

    //Установка видимости для подгруженных объектов слоя
    commit('setAllLayerObjectsVisibility', {
      layerId,
      visibility,
      visibilityProperty: getters.objectVisibilityProperty
    })
  },

  /**
   * Найти родителя у которого разрешена видимость только одного дочернего слоя
   * и скрыть все его дочерние слои, кроме того "с которого поднялись"
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  hideSiblingsForSingleChildVisibleLayer({ dispatch, getters }, { layerId }) {
    const layerSettings = getters.objectLayerSettings[layerId]
    if (!layerSettings) {
      return
    }

    const { parentId } = layerSettings
    if (!parentId) {
      return
    }

    const parentLayerSettings = getters.objectLayerSettings[parentId]
    if (parentLayerSettings && parentLayerSettings.singleChildVisibility) {
      getters
        .getLayerSettingsByParentId(parentId)
        .filter((siblingLayerSettings) => {
          return siblingLayerSettings.id !== layerId
        })
        .forEach((siblingLayerSettings) => {
          dispatch('setLayerVisibility', {
            layerId: siblingLayerSettings.id,
            visibility: false
          })
        })
      return
    }

    dispatch('hideSiblingsForSingleChildVisibleLayer', { layerId: parentId })
  },

  /**
   * Скрыть предыдущие слои с рекурсивным поднятием по дереву слоев
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  hidePrevSiblings({ dispatch, getters }, { layerId }) {
    const layerSettings = getters.objectLayerSettings[layerId]
    if (!layerSettings) {
      return
    }
    const { parentId, hidePrevSiblings, order } = layerSettings
    if (hidePrevSiblings) {
      getters
        .getLayerSettingsByParentId(parentId)
        .filter((siblingLayerSettings) => {
          return siblingLayerSettings.id !== layerId && siblingLayerSettings.order <= order
        })
        .forEach((siblingLayerSettings) => {
          dispatch('setLayerVisibility', {
            layerId: siblingLayerSettings.id,
            visibility: false
          })
        })
      return
    }

    if (!parentId) {
      return
    }

    dispatch('hidePrevSiblings', { layerId: parentId })
  },

  /**
   * Показать следующие  слои с рекурсивным поднятием по дереву слоев
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  showNextSiblings({ dispatch, getters }, { layerId }) {
    const layerSettings = getters.objectLayerSettings[layerId]
    if (!layerSettings) {
      return
    }

    const { parentId, showNextSiblings, order } = layerSettings
    if (showNextSiblings) {
      getters
        .getLayerSettingsByParentId(parentId)
        .filter((siblingLayerSettings) => {
          return siblingLayerSettings.id !== layerId && siblingLayerSettings.order > order
        })
        .forEach((siblingLayerSettings) => {
          dispatch('setLayerVisibility', {
            layerId: siblingLayerSettings.id,
            visibility: true
          })
        })
      return
    }

    if (!parentId) {
      return
    }

    dispatch('showNextSiblings', { layerId: parentId })
  },

  /**
   * Удалить следующие  слой и всех его детей + объекты, принадлежащие им
   */
  removeLayerAndChildren({ commit, getters, dispatch }, layerId) {
    commit('removeObjectLayerSetting', layerId)
    getters.getLayerSettingsByParentId(layerId).forEach((childLayer) => {
      dispatch('removeLayerAndChildren', childLayer.id)
    })
  },

  /**
   * Загрузка словаря иконок для инфослоев
   * @param {*} param0
   * @returns
   */
  loadInfoLayerIcons({ commit, getters }) {
    //Дефолтная иконка для получения белого фона
    toDataURL(require('@/assets/infolayer_default.png'), (iconDataUrl) => {
      commit('addInfoLayerIcon', {
        iconConfig: {
          id: 'infolayer_default',
          icon_file: {
            file: svgLeafletInfo(iconDataUrl)
          }
        }
      })
    })

    return Vue.http
      .get(getters.endpoint.icon, {
        params: {
          workspace: getters.workspace,
          version: getters.version
        }
      })
      .then((r) => {
        //Этот вариант использует иконку "как-есть"
        /*
        commit('setInfoLayerIcons', {
          icons: r.body.results
        })
        */

        //Этот вариант оборачивает в svg чтобы добавить белый фон
        r.body.results.forEach((iconConfig) => {
          toDataURL(iconConfig.icon_file.file, (iconDataUrl) => {
            commit('addInfoLayerIcon', {
              iconConfig: {
                ...iconConfig,
                icon_file: {
                  ...iconConfig.icon_file,
                  file: svgLeafletInfo(iconDataUrl)
                }
              }
            })
          })
        })
      })
  },

  /**
   * Обновление настроек и стилей слоев
   * @param {*} param0
   * @param {*} param1
   * @returns
   */
  updateLayerSettingsAndStyles({ commit, getters }, { layerSettings, layerStyles }) {
    return Vue.http
      .patch(
        getters.endpoint.setting(getters.settings.front_layer_layout.id),
        {
          value: {
            layerSettings,
            layerStyles
          }
        },
        {
          params: {
            workspace: getters.workspace,
            version: getters.version
          }
        }
      )
      .then((result) => {
        commit('setSettings', {
          front_layer_layout: {
            id: result.body.id,
            ...result.body.value
          }
        })
      })
  },

  loadMapData({ dispatch, getters }) {
    if (!getters.objectMapParams || !getters.objectsPeriod || !getters.objectsPeriod.borders) {
      return
    }

    let params = {
      zoom: getters.objectMapParams.zoom
    }
    if (getters.objectMapParams.zoom >= getters.settings.bbox_min_zoom) {
      params.square = getters.objectMapParams.square
    }
    if (!getters.token && getters.objectPublicFilters && getters.objectPublicFilters.length > 0) {
      params.active_filters = getters.objectPublicFilters.join(',')
    }

    //Запрос данных для карты по основным слоям
    getters.visibleLayerDataSources
      .filter((dataSource) => {
        return !IGNORE_IN_LOAD_MAP_DATA.includes(dataSource)
      })
      .forEach((dataSource) => {
        let mapParams = {
          type: dataSource,
          params
        }

        //Дополнительный параметр для источника данных группы зон
        if (!getters.objectZoneGroupMode && dataSource === 'zonegroup') {
          mapParams.isFull = 1
        }

        dispatch('getObjectMap', mapParams)
      })

    //Запрос данных для карты по инфослоям
    const infoLayers = ['info', 'streaminfo']
    infoLayers.forEach((layerType) => {
      getters.getLayerSettingsByParentId(layerType).forEach((layerSettings) => {
        if (!getters.getLayerVisibility(layerSettings.id)) {
          return
        }

        dispatch('getObjectMap', {
          type: layerType,
          params,
          layerId: layerSettings.serverId,
          //Такой странное название так как в действии getObjectMap параметр layerId уже занят
          layerSettingsId: layerSettings.id
        })
      })
    })
  },

  /**
   * Свернуть все слои
   * @param {*} param0
   */
  collapseAllLayers({ commit, getters }) {
    getters.availableLayerSettigns.forEach((layerSettings) => {
      commit('setLayerSettingsField', {
        layerId: layerSettings.id,
        fieldName: 'open',
        fieldValue: false
      })
    })
  },

  /**
   * Рекурсивная очистка слоя и его дочерних слоев
   * делалось чтобы сбросить тер деление между периодами, но пока не используется
   * @param {*} param0
   * @param {*} param1
   */
  clearLayer({ commit, dispatch, getters }, { layerId, clearRootLayer = false }) {
    const isGroupLayer = getters.isGroupLayer(layerId)
    if (isGroupLayer) {
      getters.getLayerSettingsByParentId(layerId).forEach((childLayerSettingsItem) => {
        dispatch('clearLayer', {
          layerId: childLayerSettingsItem.id,
          clearRootLayer: true
        })
      })
    }
    if (clearRootLayer) {
      commit('deleteObjectLayerSettingsItem', { layerId })
    }
  },

  /**
   * Очистка настроек слоев-зон
   * @param {*} param0
   */
  clearZoneLayerSettings({ commit, dispatch, getters }) {
    const zoneGroupLayerSettings = getters.getLayerSettingsByParentId('zonegroup')
    zoneGroupLayerSettings.forEach((zoneGroupLayerSettingsItem) => {
      const zoneGroupAdmLevelLayerSettings = getters.getLayerSettingsByParentId(zoneGroupLayerSettingsItem.id)
      zoneGroupAdmLevelLayerSettings.forEach((admLevelLayerSettings) => {
        dispatch('clearLayer', {
          layerId: admLevelLayerSettings.id,
          clearRootLayer: false
        })
      })
    })
  }
}



// WEBPACK FOOTER //
// ./src/store/objects/actionsNewStructure.js